/**
 * The **native** producer/consumer pair for entity field edits — the counterpart to
 * `useConnectionMutation`, and the shape this package is built toward.
 *
 * `useConnectionMutation` is keyed by `{ownerId, connectionPath}`, which a connection producer
 * knows because it targets exactly one connection. A field edit doesn't work that way: a
 * `setFields` names its own target and applies wherever that entity appears, so an issue title
 * edited from a side panel has to reach the panel's own document, the dashboard row behind it, and
 * anything else on the page holding that id — none of which the producer can enumerate.
 *
 * So the producer publishes and the *documents* declare where a confirmed value should land:
 *
 * - `useEntityMutation` (producer) is a plain TanStack mutation carrying its `setFields` operation
 *   as `variables`. Pending ⇒ every `useOptimisticData` consumer overlays it; error ⇒ the operation
 *   drops and every consumer rolls back together. There is **no** `dispatchConnectionMutation`
 *   here: `connection-mutation-bridge.ts` exists to mirror *Relay* commits into the mutation cache,
 *   and a native mutation is already in it.
 * - `useOptimisticData` (consumer) registers the query keys a document holds, so the producer can
 *   fold the server's values into each of them *before* its mutation leaves `pending`.
 *
 * That ordering is the whole contract. The overlay stops applying the instant the mutation settles,
 * so if `base` doesn't already reflect the write at that moment the value snaps back to
 * pre-mutation data and jumps forward again when a refetch lands.
 */
import {useMutation, type UseMutationOptions, type UseMutationResult} from '@github-ui/react-query'

import {DOCUMENT_FIELDS_PATH, type SetFieldsOperation} from './connection-operations'
import {type EntityMutationVariables, notifySettlement} from './settlement-registry'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from './pending-operations'

/**
 * Publishes a field edit as a native TanStack mutation.
 *
 * `getConfirmedOperation` must read the **server's** values out of the response. Echoing the
 * optimistic values back would settle a document to something the server never said — `titleHTML`,
 * for one, is server-derived and cannot be predicted from the raw title — and because
 * `settleConfirmedOperation` re-folds *after* revalidation, the echoed value would also overwrite
 * whatever the refetch just brought back. It looks correct until someone reloads.
 */
export function useEntityMutation<TData = void, TError = Error, TOnMutateResult = unknown>({
  mutationFn,
  getConfirmedOperation,
  onSuccess,
  ...options
}: {
  /** Performs the server write for this operation and resolves when it's committed. */
  mutationFn: (operation: SetFieldsOperation) => Promise<TData>
  /**
   * The operation carrying the server's confirmed values, or `undefined` when the response is too
   * partial to fold — in which case nothing is settled and the overlay simply drops.
   */
  getConfirmedOperation: (data: TData, operation: SetFieldsOperation) => SetFieldsOperation | undefined
} & Omit<
  UseMutationOptions<TData, TError, EntityMutationVariables, TOnMutateResult>,
  'mutationKey' | 'mutationFn'
>): UseMutationResult<TData, TError, EntityMutationVariables, TOnMutateResult> {
  return useMutation<TData, TError, EntityMutationVariables, TOnMutateResult>({
    ...options,
    mutationKey: entityFieldsMutationKey(),
    mutationFn: variables => mutationFn(variables.operation),
    // Hook-level, and awaited: a per-call `mutate(…, {onSuccess})` is not awaited by TanStack, so
    // settling there would let the mutation resolve — and the overlay drop — before the fold lands.
    onSuccess: async (data, variables, onMutateResult, context) => {
      const confirmed = getConfirmedOperation(data, variables.operation)
      if (confirmed) await notifySettlement(confirmed, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
      await onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
